const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
const merchandising = record => /camiseta|t-shirt|tshirt|sudadera|gorra|merchandising|catalogo|expositor|\bdummy\b/i.test(record.title || '');
const priorityFamilies = {
  'ASUS':['portatiles','placas base','tarjetas graficas','monitores profesionales','routers domesticos','teclados'],
  'TP-Link':['puntos de acceso','switches gestionables','routers domesticos','camaras ip','repetidores wifi','no gestionable'],
  'Ajax Systems':['kits','detectores','sirenas','teclados','centrales','camaras ip'],
  'HikVision':['camaras bullet','camaras domo','grabadores','domos motorizados','monitores','control de accesos y greenpass'],
  'Dahua':['camaras ip','grabadores ip','camaras ptz','pantallas interactivas','control de accesos','soportes'],
};

// These are presentation aliases only. Links retain every exact supplier product type.
export function familyLabel(type) {
  const key = normalize(type);
  if (['portatil', 'portatiles'].includes(key)) return 'Portátiles';
  if (['placa base', 'placas base'].includes(key)) return 'Placas base';
  if (['raton','ratones'].includes(key)) return 'Ratones';
  if (['teclado','teclados'].includes(key)) return 'Teclados';
  if (['central','centrales'].includes(key)) return 'Centrales';
  if (['caja de conexiones', 'cajas de conexiones'].includes(key)) return 'Cajas de conexiones';
  if (['monitores digital signage', 'pantallas digital signage'].includes(key)) return 'Digital signage';
  if (['pantallas lcd profesionales', 'monitor lcd profesional'].includes(key)) return 'Monitores profesionales';
  if (['pantallas interactivas profesionales', 'monitores interactivos'].includes(key)) return 'Pantallas interactivas';
  if (['grabadoras cctv ip', 'grabadores ip'].includes(key)) return 'Grabadores IP';
  if (['domos ptz ip', 'camaras ptz ip'].includes(key)) return 'Cámaras PTZ';
  if (['sistemas de alimentacion solar', 'sistema de alimentacion solar'].includes(key)) return 'Alimentación solar';
  return type;
}

export function buildFamilies(records) {
  const groups = new Map();
  for (const record of records) {
    const types = record.facets?.product_type || [record.customProductType || record.productType];
    for (const type of new Set(types.filter(Boolean))) {
      const label = familyLabel(type);
      const key = normalize(label);
      if (!groups.has(key)) groups.set(key, {key, label, types: new Set(), records: new Map()});
      const group = groups.get(key);
      group.types.add(type);
      group.records.set(record.id, record);
    }
  }
  return [...groups.values()].map(group => {
    const entries = [...group.records.values()];
    const candidates = entries.filter(record => record.image && !merchandising(record));
    const representative = candidates.find(r => r.stockStatus === 'in_stock') || candidates[0];
    return {key:group.key,label:group.label,types:[...group.types],count:entries.length,image:representative?.image,product:representative};
  }).sort((a,b) => b.count-a.count || a.label.localeCompare(b.label,'es'));
}

export function familyURL(base, types) {
  const url = new URL(base);
  url.searchParams.delete('nf_product_type');
  url.searchParams.delete('nf_page');
  url.searchParams.delete('page');
  for (const type of types) url.searchParams.append('nf_product_type', type);
  url.hash = 'nb-catalog';
  return url;
}

class NetisecBrandPage extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.groups = [];
    this.filter = document.querySelector('netisec-collection-filters');
    this.root = this.filter?.closest('.product-grid-container');
    this.grid = this.root?.querySelector('.product-grid');
    this.select = this.querySelector('select');
    const signal = this.controller.signal;
    this.addEventListener('click', event => {
      const link = event.target.closest('[data-family],[data-all-families]');
      if (!link || !this.filter?.ready || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.choose(link.dataset.family || '');
    }, {signal});
    this.select.addEventListener('change', () => this.choose(this.select.value), {signal});
    window.addEventListener('popstate', () => queueMicrotask(() => this.refresh()), {signal});
    if (this.root && this.grid) {
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.root, {attributes:true,attributeFilter:['data-nf-loading']});
      this.observer.observe(this.grid, {attributes:true,attributeFilter:['data-nf-total']});
    }
    this.refresh();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }
  refresh() {
    if (!this.filter?.ready) {
      if (this.filter?.failed) this.classList.remove('nb-families-pending');
      return;
    }
    this.classList.remove('nb-families-pending');
    if (!this.filter.state.sort && (this.records !== this.filter.records || this.lastSort)) {
      const priority = {in_stock:0,low:1,out_of_stock:2};
      const ordered = [...this.filter.records].sort((a,b)=>(priority[a.stockStatus]??3)-(priority[b.stockStatus]??3)||a.position-b.position);
      if (ordered.some((item,index)=>item.id!==this.filter.records[index].id)) {
        this.filter.records = ordered;
        this.filter.draw();
      }
    }
    this.lastSort = this.filter.state.sort;
    if (this.records !== this.filter.records) {
      this.records = this.filter.records;
      this.groups = buildFamilies(this.records);
      this.root.toggleAttribute('data-nb-single-vendor',new Set(this.records.map(item=>normalize(item.vendor))).size===1);
      this.render();
    }
    const selected = [...(this.filter.state.options.get('product_type') || [])].map(normalize).sort();
    const group = this.groups.find(item => JSON.stringify(item.types.map(normalize).sort()) === JSON.stringify(selected));
    for (const link of this.querySelectorAll('[data-family]')) {
      link.setAttribute('aria-current', group?.key === link.dataset.family ? 'true' : 'false');
      link.href = familyURL(location.href, this.groups.find(item=>item.key===link.dataset.family).types);
    }
    const custom = this.select.querySelector('[data-custom]');
    custom?.remove();
    if (selected.length && !group) {
      const option = new Option('Selección personalizada', '__custom');
      option.disabled = true;
      option.dataset.custom = '';
      this.select.add(option);
    }
    this.select.value = group?.key || (selected.length ? '__custom' : '');
    this.querySelector('[data-all-families]').href = familyURL(location.href, []);
    const query = this.root.querySelector('[data-query]');
    if (query) query.placeholder = `Buscar en ${this.dataset.brand}`;
  }
  render() {
    const number = new Intl.NumberFormat('es');
    const priorities = priorityFamilies[this.dataset.brand] || [];
    const available = this.groups.filter(group => group.image && ![normalize(this.dataset.brand),normalize(this.dataset.brand).replace(' systems','')].includes(group.key));
    const priorityGroups = priorities.map(key=>available.find(group=>group.key===key)).filter(Boolean);
    const shown = [...priorityGroups,...available.filter(group=>!priorities.includes(group.key))].slice(0,6);
    if (this.dataset.curated !== 'true' && shown.length) {
      const products = shown.slice(0,3).map(group=>group.product);
      const art = this.querySelector('.nb-art');
      art.className = `nb-art nb-art--${products.length}`;
      art.innerHTML = products.map((product,index)=>`<a class="nb-art__product nb-art__product--${index+1}" href="${escape(product.url)}" aria-label="${escape(product.title)}"><img class="nb-art__image" src="${escape(product.image)}" alt="${escape(product.title)}" width="540" height="540"></a>`).join('');
    }
    this.querySelector('[data-family-list]').innerHTML = shown.map(group => `<a class="nb-family" href="${escape(familyURL(location.href,group.types))}" data-family="${escape(group.key)}" aria-current="false"><span class="nb-family__image"><img src="${escape(group.image)}" alt="" width="180" height="120" loading="lazy"></span><span class="nb-family__label">${escape(group.label)}</span><span class="nb-family__count">${number.format(group.count)} ${group.count===1?'producto':'productos'}</span></a>`).join('');
    this.querySelector('.nb-families').hidden = shown.length < 2;
    this.select.innerHTML = '<option value="">Todas las familias</option>' + [...this.groups].sort((a,b)=>a.label.localeCompare(b.label,'es')).map(group=>`<option value="${escape(group.key)}">${escape(group.label)} (${number.format(group.count)})</option>`).join('');
    this.select.closest('label').hidden = this.groups.length < 2;
  }
  choose(key) {
    const types = this.groups.find(item => item.key === key)?.types || [];
    if (!this.filter?.ready) { location.href = familyURL(location.href,types); return; }
    this.filter.state.options.set('product_type',new Set(types));
    this.filter.changed();
    this.refresh();
    const target = this.querySelector('#nb-catalog');
    target.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    target.focus({preventScroll:true});
  }
}
if (!customElements.get('netisec-brand-page')) customElements.define('netisec-brand-page',NetisecBrandPage);
